import { Chess } from 'chess.js';
import { evaluate } from './evaluation';

// Move quality classifications
export const MOVE_QUALITY = {
  brilliant: {
    label: 'Brilliant!',
    color: '#00D9FF',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500',
    icon: '★★',
    description: 'An exceptional move that creates winning chances!'
  },
  great: {
    label: 'Great Move!',
    color: '#22C55E',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500',
    icon: '★',
    description: 'A very strong move - well played!'
  },
  good: {
    label: 'Good',
    color: '#84CC16',
    bgColor: 'bg-lime-500/20',
    borderColor: 'border-lime-500',
    icon: '✓',
    description: 'A solid move that maintains your position.'
  },
  book: {
    label: 'Book Move',
    color: '#A855F7',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500',
    icon: '📚',
    description: 'A standard opening move played by masters.'
  },
  inaccuracy: {
    label: 'Inaccuracy',
    color: '#EAB308',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500',
    icon: '?!',
    description: 'Not the best move - you missed a better option.'
  },
  mistake: {
    label: 'Mistake',
    color: '#F97316',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500',
    icon: '?',
    description: 'This move gives your opponent an advantage.'
  },
  blunder: {
    label: 'Blunder!',
    color: '#EF4444',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500',
    icon: '??',
    description: 'A serious mistake - this could cost the game!'
  }
};

// Piece names for explanations
const PIECE_NAMES = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king'
};

// Analyze a move and return quality assessment
export function analyzeMoveQuality(gameBefore, moveFrom, moveTo, suggestedMove, isOpeningPhase = false) {
  try {
    // Create a copy to analyze
    const beforeCopy = new Chess(gameBefore.fen());
    const evalBefore = evaluate(beforeCopy);
    
    // Make the actual move
    const actualMove = beforeCopy.move({ from: moveFrom, to: moveTo, promotion: 'q' });
    if (!actualMove) return null;
    
    const evalAfter = evaluate(beforeCopy);
    
    // Calculate evaluation change from player's perspective
    const playerIsWhite = gameBefore.turn() === 'w';
    const evalChange = playerIsWhite ? (evalAfter - evalBefore) : (evalBefore - evalAfter);
    
    // Check if this was the suggested (best) move
    const isBestMove = suggestedMove && 
      moveFrom === suggestedMove.from && 
      moveTo === suggestedMove.to;
    
    // Determine move quality based on centipawn loss
    let quality;
    let explanation;
    
    // If it's an opening book move
    if (isOpeningPhase && suggestedMove?.isBookMove && isBestMove) {
      quality = 'book';
      explanation = `You played a standard opening move. ${getPositionalComment(actualMove)}`;
    }
    // Best move
    else if (isBestMove) {
      if (evalChange > 100) {
        quality = 'brilliant';
        explanation = `Excellent! This was the best move and ${getBrilliantReason(actualMove, evalChange)}`;
      } else if (evalChange > 30) {
        quality = 'great';
        explanation = `Great choice! ${getPositionalComment(actualMove)}`;
      } else {
        quality = 'good';
        explanation = `Solid move. ${getPositionalComment(actualMove)}`;
      }
    }
    // Not the best move - calculate loss
    else {
      // Get what the best move's evaluation would have been
      const bestCopy = new Chess(gameBefore.fen());
      if (suggestedMove) {
        bestCopy.move({ from: suggestedMove.from, to: suggestedMove.to, promotion: 'q' });
      }
      const bestEval = evaluate(bestCopy);
      const centipawnLoss = playerIsWhite ? (bestEval - evalAfter) : (evalAfter - bestEval);
      
      if (centipawnLoss < 20) {
        quality = 'good';
        explanation = `A reasonable move. ${getPositionalComment(actualMove)}`;
      } else if (centipawnLoss < 50) {
        quality = 'inaccuracy';
        explanation = getMissedOpportunityExplanation(actualMove, suggestedMove, centipawnLoss);
      } else if (centipawnLoss < 150) {
        quality = 'mistake';
        explanation = getMistakeExplanation(actualMove, suggestedMove, centipawnLoss);
      } else {
        quality = 'blunder';
        explanation = getBlunderExplanation(actualMove, suggestedMove, centipawnLoss);
      }
    }
    
    return {
      quality,
      ...MOVE_QUALITY[quality],
      explanation,
      evalBefore,
      evalAfter,
      evalChange,
      isBestMove,
      actualMove: actualMove.san,
      suggestedMove: suggestedMove?.san
    };
  } catch (error) {
    console.error('Error analyzing move quality:', error);
    return null;
  }
}

// Generate reason for brilliant moves
function getBrilliantReason(move, evalChange) {
  if (move.captured) {
    return `wins material with a strong capture!`;
  }
  if (move.san.includes('+')) {
    return `creates a powerful attack with check!`;
  }
  if (move.san === 'O-O' || move.san === 'O-O-O') {
    return `safely castles while improving position!`;
  }
  if (evalChange > 200) {
    return `dramatically improves your position!`;
  }
  return `creates winning chances!`;
}

// Generate positional comment for good moves
function getPositionalComment(move) {
  const comments = [];
  
  if (move.captured) {
    comments.push(`You captured their ${PIECE_NAMES[move.captured]}.`);
  }
  
  if (move.san.includes('+')) {
    comments.push('Check!');
  }
  
  if (move.san === 'O-O' || move.san === 'O-O-O') {
    comments.push('Castling keeps your king safe and connects your rooks.');
  }
  
  const centerSquares = ['d4', 'd5', 'e4', 'e5'];
  if (centerSquares.includes(move.to)) {
    comments.push('Good center control.');
  }
  
  if ((move.piece === 'n' || move.piece === 'b') && 
      ['b1', 'g1', 'c1', 'f1', 'b8', 'g8', 'c8', 'f8'].includes(move.from)) {
    comments.push('Nice piece development!');
  }
  
  if (comments.length === 0) {
    const genericComments = [
      'Keeps the pressure on.',
      'A logical continuation.',
      'Maintains your position.',
      'A sensible choice.'
    ];
    comments.push(genericComments[Math.floor(Math.random() * genericComments.length)]);
  }
  
  return comments.join(' ');
}

// Explain missed opportunities (inaccuracies)
function getMissedOpportunityExplanation(actualMove, suggestedMove, _loss) {
  if (!suggestedMove) {
    return `This move is okay, but there was a slightly better option.`;
  }
  
  const better = suggestedMove.san || `${suggestedMove.from}-${suggestedMove.to}`;
  
  if (suggestedMove.captured) {
    return `You could have captured with ${better} instead. That would have been stronger.`;
  }
  
  if (suggestedMove.san?.includes('+')) {
    return `${better} would have given check - a more aggressive option!`;
  }
  
  return `${better} was slightly better here. Your move is okay but misses a small opportunity.`;
}

// Explain mistakes
function getMistakeExplanation(actualMove, suggestedMove, _loss) {
  if (!suggestedMove) {
    return `This move weakens your position. Try to think about what your opponent might do next.`;
  }
  
  const better = suggestedMove.san || `${suggestedMove.from}-${suggestedMove.to}`;
  
  if (actualMove.captured && suggestedMove.captured) {
    return `You captured, but ${better} was a better capture. Always look for the best exchange!`;
  }
  
  if (suggestedMove.san?.includes('+')) {
    return `You missed a chance to give check with ${better}! Checks often create strong threats.`;
  }
  
  return `${better} was much stronger. This move gives your opponent a chance to improve their position.`;
}

// Explain blunders
function getBlunderExplanation(actualMove, suggestedMove, loss) {
  if (!suggestedMove) {
    return `This is a serious mistake! Your opponent now has a big advantage. Always check if your pieces are safe before moving.`;
  }
  
  const better = suggestedMove.san || `${suggestedMove.from}-${suggestedMove.to}`;
  
  // Check if we're hanging a piece
  if (loss > 250) {
    return `Oops! This loses significant material. ${better} was needed. Always ask "Is my piece safe after this move?"`;
  }
  
  if (suggestedMove.san?.includes('#')) {
    return `You missed checkmate with ${better}! Always look for forcing moves like checks and captures.`;
  }
  
  return `This is a big mistake. ${better} was much better. Try to see what your opponent threatens after each move.`;
}

// Get move quality for display in game review
export function getQualityForReview(quality) {
  return MOVE_QUALITY[quality] || MOVE_QUALITY.good;
}
